<script setup lang="ts">
import AdminSupplierDetails from '@/components/admin/supplier/details/AdminSupplierDetails.vue';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { adminSupplierService } from '@/services/admin/admin-supplier-service.ts';
import { PhCaretDoubleLeft, PhCaretDoubleRight } from '@phosphor-icons/vue';
import { models } from 'cesg-shared';
import { onActivated, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

type Supplier = models.AdminSupplierBasicResponse[number];

interface ColumnData {
  key: keyof Supplier;
  label: string;
  sortable?: boolean;
}

const listIsPopulated = ref(false);
const detailsAreReady = ref(false);
const isSupplierListHidden = ref(false);
const isCreating = ref(false);

const toggleSupplierList = () => {
  isSupplierListHidden.value = !isSupplierListHidden.value;
};

const suppliers = ref<models.AdminSupplierBasicResponse>([]);

const columns: ColumnData[] = [
  {
    key: 'supplierNumber',
    label: t('adminSupplierRegister.supplierNumber'),
    sortable: true,
  },
  {
    key: 'supplierName',
    label: t('adminSupplierRegister.supplierName'),
    sortable: true,
  },
  {
    key: 'organisationNumber',
    label: t('adminSupplierRegister.organisationNumber'),
    sortable: true,
  },
];

const selectedSupplierId = ref<string | null>(null);

function selectSupplier(id: string) {
  selectedSupplierId.value = id;
  isCreating.value = false;
}

function handleDetailsReady() {
  detailsAreReady.value = true;
}

function handleCreateNew() {
  selectedSupplierId.value = null;
  isCreating.value = true;
  detailsAreReady.value = true;
}

const handleSupplierDeleted = (deletedId: string) => {
  suppliers.value = suppliers.value.filter((s) => s.id !== deletedId);

  if (suppliers.value.length > 0) {
    selectSupplier(suppliers.value[0].id);
  } else {
    selectedSupplierId.value = null;
    isCreating.value = false;
  }
};

const handleSupplierCreated = async (newId: string) => {
  if (newId) {
    selectedSupplierId.value = newId;
  }
  isCreating.value = false;
  await loadData();
  if (newId) {
    selectSupplier(newId);
  }
};

const handleCancelCreate = () => {
  isCreating.value = false;
  if (suppliers.value.length > 0) {
    selectSupplier(suppliers.value[0].id);
  } else {
    selectedSupplierId.value = null;
  }
};

const handleUpdated = async (id: string) => {
  await loadData();
  selectedSupplierId.value = id;
};

const buttonWidth = 40;
const collapsedWidth = `${buttonWidth}px`;
const defaultWidth = '28%';
const marginRightPx = 10;

const supplierListColumnStyle = ref({
  flex: `0 0 ${defaultWidth}`,
  maxWidth: defaultWidth,
  transition: 'flex 0s, max-width 0s',
});

const buttonLeftOffset = ref('0px');

watch(
  isSupplierListHidden,
  (isHidden) => {
    supplierListColumnStyle.value = {
      flex: isHidden ? `0 0 ${collapsedWidth}` : `0 0 ${defaultWidth}`,
      maxWidth: isHidden ? collapsedWidth : defaultWidth,
      transition: 'flex 0.3s ease-in-out, max-width 0.3s ease-in-out',
    };

    if (isHidden) {
      const collapsedOffset = buttonWidth - buttonWidth / 2 + marginRightPx;
      buttonLeftOffset.value = `${collapsedOffset}px`;
    } else {
      const buttonCorrection = buttonWidth / 2 - marginRightPx;
      buttonLeftOffset.value = `calc(${defaultWidth} - ${buttonCorrection}px)`;
    }
  },
  { immediate: true },
);

const loadData = async () => {
  try {
    suppliers.value = await adminSupplierService.getAll();

    if (suppliers.value.length > 0 && !selectedSupplierId.value) {
      selectSupplier(suppliers.value[0].id);
    }

    listIsPopulated.value = true;
  } catch (error) {
    console.error('Failed to load suppliers', error);
  }
};

onMounted(() => {
  loadData();
});

onActivated(() => {
  loadData();
});
</script>

<template>
  <div class="columns">
    <div class="column supplier-list-column" :style="supplierListColumnStyle">
      <div v-show="!isSupplierListHidden">
        <h2 class="title is-4">{{ t('adminSupplierRegister.title') }}</h2>

        <div class="new-supplier">
          <button @click="handleCreateNew">
            {{ t('adminSupplierRegister.addSupplier') }}
          </button>
        </div>

        <SimpleTable :columns="columns" :data="suppliers" :enable-search="true">
          <template #tbody="{ data }">
            <tr
              v-for="supplier in data"
              :key="supplier.id"
              @click="selectSupplier(supplier.id)"
              :class="{ active: selectedSupplierId === supplier.id }"
              style="cursor: pointer"
            >
              <td v-for="(column, index) in columns" :key="index">
                {{ supplier[column.key] }}
              </td>
            </tr>
          </template>
        </SimpleTable>
      </div>
    </div>

    <div
      v-if="!isCreating && detailsAreReady"
      class="toggle-button-wrapper-absolute"
      :style="{ left: buttonLeftOffset }"
    >
      <button
        class="toggle-button"
        @click="toggleSupplierList"
        :title="t('adminSupplierRegister.toggleList')"
      >
        <PhCaretDoubleLeft
          v-if="!isSupplierListHidden"
          size="24"
          weight="bold"
        />
        <PhCaretDoubleRight
          v-if="isSupplierListHidden"
          size="24"
          weight="bold"
        />
      </button>
    </div>

    <div class="column details-column">
      <AdminSupplierDetails
        v-if="listIsPopulated"
        :supplier-id="selectedSupplierId"
        :create-mode="isCreating"
        :large-font="isSupplierListHidden"
        @deleted="handleSupplierDeleted"
        @created="handleSupplierCreated"
        @cancel-create="handleCancelCreate"
        @updated="handleUpdated"
        @ready="handleDetailsReady"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.new-supplier {
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0;
  align-items: center;
  min-height: 42px;

  button {
    background-color: var(--admin-projects-add-user);
    color: rgba(0, 0, 0, 0.8);
    border: 1px solid #333;
    border-radius: 6px;
    padding: 0 1.2rem;
    height: 42px;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      background-color: var(--admin-projects-add-user-hover);
      border-color: #000;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      border-color: #333;
    }
  }
}

.active {
  background-color: var(--admin-suppliers-green-active-row) !important;
}

.columns {
  display: flex;
  position: relative;
}

.column {
  padding: 1rem;
}

.supplier-list-column {
  padding-right: 1rem;
  overflow: hidden;
}

.details-column {
  flex: 1;
  min-width: 0;
  position: relative;
  padding-left: 1rem;
}

.toggle-button-wrapper-absolute {
  position: absolute;
  top: 853px;
  transform: translateY(-50%);
  z-index: 50;
  transition: left 0.3s ease-in-out;
}

.toggle-button {
  background-color: var(--admin-suppliers-green-button-bg);
  border: 1px solid var(--admin-suppliers-green-button-border-hover);
  border-radius: 5px;
  padding: 0.25rem;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  transition: background-color 0.2s ease;
}

.toggle-button:hover {
  background-color: var(--admin-suppliers-green-button-border-hover);
}

.title.is-4 {
  margin-bottom: 1rem;
}

.supplier-list-column {
  :deep(th:nth-child(1)) {
    width: 85px;
    min-width: 60px;
  }

  :deep(td:nth-child(1)) {
    width: 40px;
    min-width: 40px;
    white-space: nowrap;
  }
}
</style>
